import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// @import Custom Components
import { WriterlyAuthLayout } from './Layout';
import { WriterlyAuthWordsCarousel } from './WordsContent';
import { WriterlyAuthLogo } from './Logo';
import { WriterlyAuthForm } from './Form';
import { WriterlyBackgroundLoader } from '../../shared/Loader/Background';
import { WriterlyGoogleAuth } from './Google';

// @import Mui Components
import { Typography, Button } from '@mui/material';

// @import validations
import { camalize } from '../../utils/validations';

// @import Reducers
import { confirmEmail, login } from '../../redux/slices/auth';

const WriterlyAuthLogin = ({ data }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const formPage = camalize(pathname.split('/')[1]) || 'login';
  const loading = useSelector((state) => state.auth.login.loading);
  const accessToken = useSelector((state) => state.user.accessToken);
  const firstTime = useSelector((state) => state.user.data.is_first_time);
  const isAccountOwner = useSelector(
    (state) => state.user.data.is_account_owner
  );
  const email = useSelector(
    (state) => state.auth.verifyEmail.sentSinceRegister
  );
  const loadingVerify = useRef(true);

  const formFields = { email: '', password: '' };
  const contentWords = [
    t('carouselWords.create'),
    t('carouselWords.collaborate'),
    t('carouselWords.generate'),
    t('carouselWords.market'),
    t('carouselWords.design'),
    t('carouselWords.advertise'),
    t('carouselWords.write'),
  ];
  useEffect(() => {
    validateConfirmEmail();
  }, []);

  useEffect(() => {
    if (accessToken) {
      const url = firstTime && isAccountOwner ? '/signup-plan' : '/app/create';
      navigate(url);
    }
  }, [accessToken]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (email && token) navigate(`/verify-email/${token}`);
  }, [email]);

  const validateConfirmEmail = () => {
    const verifyToken = searchParams.get('verifyEmail');
    if (verifyToken && loadingVerify.current) {
      dispatch(confirmEmail(verifyToken));
      loadingVerify.current = false;
    }
    window.history.replaceState(null, '', `/${formPage}`);
  };

  const loginForm = () => {
    const handleSubmit = (values, actions) => {
      dispatch(login(values));
      actions.resetForm();
    };

    const formValidation = Yup.object().shape({
      email: Yup.string()
        .email(t(`${formPage}.form.fields.email.notValid`))
        .required(t(`${formPage}.form.fields.email.required`)),
      password: Yup.string().required(
        t(`${formPage}.form.fields.password.required`)
      ),
    });

    return (
      <div
        className="flex flex-col justify-between h-full"
        style={{ fontFamily: 'DM Sans' }}
      >
        <WriterlyBackgroundLoader visible={loading} />
        <div className="form mt-24 lg:mt-36">
          <WriterlyAuthLogo />
          <div className="title flex justify-center mt-10">
            <Typography variant="h2" className="text-black">
              {t('login.title')}
            </Typography>
          </div>
          <div className="inputs flex flex-col justify-center mt-10">
            <WriterlyAuthForm
              formFields={formFields}
              validationSchema={formValidation}
              handleSubmit={handleSubmit}
              formPage={formPage}
              extraFields={
                <Typography
                  variant="b3"
                  className="font-bold uppercase hover:underline cursor-pointer flex justify-end text-[#7A818C]"
                  onClick={() => navigate('/password-recovery')}
                  data-testid={'login-passwordRecovery'}
                >
                  {t('forgotPassword')}
                </Typography>
              }
              buttons={
                <>
                  <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    className="mt-5"
                    type="submit"
                  >
                    {t('continue')}
                  </Button>
                  <WriterlyGoogleAuth buttonProps={{ text: 'signin' }} />
                </>
              }
            />
          </div>
        </div>
        <div className="flex flex-col items-center mt-8 mb-32 pb-10">
          <Typography variant="b3" className="uppercase text-[#7A818C]">
            {t('dontHaveAccount')}
          </Typography>
          <Typography
            variant="b3"
            className="font-bold uppercase text-[#7A818C] hover:underline cursor-pointer"
            onClick={() => navigate('/sign-up')}
            data-testid={'login-createAccount'}
          >
            {t('createAccount')}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <WriterlyAuthLayout
      leftContent={loginForm()}
      rightContent={
        <WriterlyAuthWordsCarousel
          autoRotate={true}
          words={contentWords}
          fixedWord={t('carouselWords.lets')}
        />
      }
    />
  );
};

WriterlyAuthLogin.propTypes = {
  data: PropTypes.any,
};

export default WriterlyAuthLogin;
